import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  MyJobPacket,
  MyJobPacketDetail,
  JobPacketAcknowledgeResponse,
  PlanInitialInput,
} from '@/lib/types';

const ME_API = '/api/v1/me';

export function useMyJobPackets() {
  return useQuery<MyJobPacket[]>({
    queryKey: ['my-job-packets'],
    queryFn: async () => {
      const response = await apiClient.get<MyJobPacket[]>(
        `${ME_API}/job-packets/`,
      );
      return response.data;
    },
  });
}

export function useMyJobPacket(id: number) {
  return useQuery<MyJobPacketDetail>({
    queryKey: ['my-job-packets', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<MyJobPacketDetail>(
        `${ME_API}/job-packets/${id}/`,
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useAcknowledgeMyJobPacket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      plan_initials,
    }: {
      id: number;
      plan_initials: PlanInitialInput[];
    }): Promise<JobPacketAcknowledgeResponse> => {
      const response = await apiClient.post<JobPacketAcknowledgeResponse>(
        `${ME_API}/job-packets/${id}/acknowledge/`,
        { plan_initials },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-job-packets'] });
      qc.invalidateQueries({ queryKey: ['my-dashboard'] });
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
    },
  });
}
